import type { Organisation } from '@/types/organisations.ts';

/* The key the Vue console wrote, so a choice survived the migration. It holds the last concrete organisation. */
const ORGANISATION_KEY = 'active_organisation';
/* Set while "All organisations" is chosen; the concrete one above is kept for the pages that need one. */
const ALL_KEY = 'active_organisation_all';

export interface StoredOrganisationChoice {
  organisation: Organisation | null;
  all: boolean;
}

function isOrganisation(value: unknown): value is Organisation {
  if (typeof value !== 'object' || value === null) return false;

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.id === 'string' &&
    typeof candidate.name === 'string' &&
    typeof candidate.type === 'string'
  );
}

/* Storage can throw in a browser that blocks site data; forgetting the choice beats not rendering. */
export function readStoredOrganisationChoice(): StoredOrganisationChoice {
  try {
    const stored = window.localStorage.getItem(ORGANISATION_KEY);
    const parsed: unknown = stored ? JSON.parse(stored) : null;

    return {
      organisation: isOrganisation(parsed) ? parsed : null,
      all: window.localStorage.getItem(ALL_KEY) === 'true',
    };
  } catch {
    return { organisation: null, all: false };
  }
}

export function writeStoredOrganisationChoice({
  organisation,
  all,
}: StoredOrganisationChoice): void {
  try {
    if (organisation) {
      const { id, name, type } = organisation;
      window.localStorage.setItem(ORGANISATION_KEY, JSON.stringify({ id, name, type }));
    } else {
      window.localStorage.removeItem(ORGANISATION_KEY);
    }

    if (all) {
      window.localStorage.setItem(ALL_KEY, 'true');
    } else {
      window.localStorage.removeItem(ALL_KEY);
    }
  } catch {
    /* The console still switches; it just forgets between visits. */
  }
}
